import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpCircleIcon, CheckIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';
import { billingApi } from '../services/api';
import { useAccountPlan, useAccountUsage } from '../hooks/useAccountUsage';
import { useSubscriptionSummary } from '../hooks/useSubscriptionSummary';
import type { PlanCatalogPlan } from '../types/billing';
import { useFlag } from '../hooks/useFeatureFlags';

/**
 * The Innovate billing model (as built — tasks 139/141/142, catalog task 232):
 *   - A tiered monthly plan FLOOR (Starter / Growth / Scale / Enterprise —
 *     there is no free tier; a new account has no plan until one is chosen).
 *   - ENDPOINT EXTRAS: each tier includes N endpoints; deploying beyond N bills a
 *     per-tier monthly fee per extra endpoint (extras = max(0, deployed − included)).
 *   - USAGE COST: every external LLM dollar is billed at (actual cost × per-tier
 *     markup), additive to the floor, from the first dollar — there is NO free
 *     usage allowance band and NO compute/token cap.
 *
 * Live, account-specific numbers (current plan, included/used endpoints, accrued
 * usage cost) come from JWT plan limits (AuthContext) plus the current-period
 * usage summary (GET /api/accounts/usage). The plan cards AND the current
 * plan's feature chips render from the live AMS catalog
 * (GET /api/billing/plans) — never from hardcoded plan data. The only
 * client-side plan copy is the tagline voice map below.
 *
 * Task 676 note: the feature chips moved off the dropped JWT `features[]` claim
 * to the catalog entry. The `plan` slug and `limits{}` reads above are the last
 * two claim dependencies here; they move to `GET /api/accounts/usage` once task
 * 680 extends it with the plan and its limits.
 */

/** Convert basis-points markup (e.g. 12500) to a human multiplier string ("1.25×"). */
function markupMultiplier(bps: number | undefined): number | null {
  if (bps === undefined || bps < 0) return null;
  return bps / 10000;
}

function formatUsd(dollars: number): string {
  return `$${dollars.toFixed(2)}`;
}

function formatCentsAsUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** Whole-dollar price rendering for plan cards ($99, not $99.00). */
function formatCentsAsWholeUsd(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? `$${dollars}` : formatUsd(dollars);
}

/**
 * Marketing voice per plan slug — the ONLY plan copy that lives client-side
 * (kv decision, plan-catalog report open question 2). Every number and
 * capability on the cards comes from the fetched catalog; unknown slugs
 * simply render without a tagline.
 */
const PLAN_TAGLINES: Record<string, string> = {
  free: 'Included with every account',
  starter: 'Launch your first apps',
  growth: 'Embed beyond the platform',
  scale: 'Real-time, multi-step at scale',
  enterprise: 'Tailored limits and support',
};

/**
 * The Free plan (task 1021), recognised from the catalog rather than from a
 * hardcoded plan list: the `free` slug at $0 that AMS does NOT provision through
 * Stripe. `selfServe: false` and `basePriceCents: 0` is also Enterprise's shape,
 * which is why the slug is what separates the two — Free is a real plan every
 * account holds, Enterprise is a contact-sales tier.
 */
function isFreePlan(plan: PlanCatalogPlan): boolean {
  return plan.slug === 'free' && plan.basePriceCents === 0 && !plan.selfServe;
}

/** Derive the card bullet list from plan data: limits first, then capabilities. */
function planCardBullets(plan: PlanCatalogPlan): string[] {
  const bullets: string[] = [];

  const maxEndpoints = plan.limits['max_endpoints'];
  if (maxEndpoints === -1) {
    bullets.push('Custom endpoint volume');
  } else if (maxEndpoints !== undefined) {
    bullets.push(`${maxEndpoints} endpoint${maxEndpoints === 1 ? '' : 's'} included`);
  }

  const extraPriceCents = plan.limits['extra_endpoint_price_cents'];
  if (extraPriceCents !== undefined && extraPriceCents > 0) {
    bullets.push(`${formatCentsAsWholeUsd(extraPriceCents)}/mo per extra endpoint`);
  }

  const markup = markupMultiplier(plan.limits['model_cost_markup_bps']);
  if (markup !== null && markup > 0) {
    bullets.push(`Usage billed at ${markup}× LLM cost`);
  }

  bullets.push(...plan.features.map((feature) => feature.name));
  return bullets;
}

/**
 * BillingPage — the proactive door to upgrading (/settings/billing).
 *
 * Renders the current plan from JWT claims (plan, limits, features via
 * AuthContext), live usage from GET /api/accounts/usage (deployed endpoints +
 * accrued LLM usage cost), the AMS-driven plan catalog, and owner-only Upgrade
 * (Stripe Checkout) / Manage billing (Stripe Billing Portal) actions on the
 * existing billing session plumbing. No card forms — Stripe hosts all payment UI.
 */
export default function BillingPage() {
  const [redirecting, setRedirecting] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);

  // Billing affordances are gated by innovate.fe.billing.page, whose
  // role-owner cohort is the owner check (task 692). The role claim is no
  // longer read here: evaluation is uniform and, as a permission-type flag,
  // fails closed during an Unleash outage.
  const canManageBilling = useFlag('innovate.fe.billing.page');
  // Plan + limits come from GET /api/accounts/usage (task 676 — previously the
  // JWT `plan` / `limits` claims). No plan slug = no active subscription (there
  // is no free tier); the account fails closed service-side until one is chosen.
  const { plan, limits, isPending: planPending } = useAccountPlan();
  const planSlug = plan?.toLowerCase() || null;
  const hasNoPlan = !planSlug;

  // --- As-built billing inputs from JWT plan limits ---------------------------
  const includedEndpoints = limits['max_endpoints']; // -1 = unlimited
  const hasFiniteEndpointCap =
    includedEndpoints !== undefined && includedEndpoints !== -1 && includedEndpoints >= 0;
  const extraEndpointPriceCents = limits['extra_endpoint_price_cents'];
  const markup = markupMultiplier(limits['model_cost_markup_bps']);

  // The shared query — it also supplies the plan and limits above.
  const { data: usage } = useAccountUsage();

  // --- Billing truth is Stripe (task 942) ------------------------------------
  // The same owner-gated subscription-summary read the Subscription page uses,
  // through the shared hook (task 964). Absent (not deployed / not an owner) →
  // the page renders exactly as before.
  const { data: summary } = useSubscriptionSummary(!hasNoPlan);

  // --- Live plan catalog (task 232) -------------------------------------------
  const {
    data: catalog,
    isLoading: catalogLoading,
    isError: catalogError,
  } = useQuery({
    queryKey: ['planCatalog'],
    queryFn: async () => {
      const res = await billingApi.getPlans();
      return res.data.data.plans;
    },
    staleTime: 10 * 60_000,
    retry: 1,
  });

  const currentPlan = catalog?.find((plan) => plan.slug === planSlug);

  /**
   * The current plan's features, from the account's catalog entry (task 676).
   * Previously read from the JWT `features[]` claim, which Phase 1 drops; the
   * catalog carries the same plan-derived list and already includes the display
   * name, so no key→name lookup is needed.
   */
  const features = currentPlan?.features ?? [];

  const planLabel = currentPlan?.name ?? (planSlug ? planSlug.charAt(0).toUpperCase() + planSlug.slice(1) : null);

  // Billing truth is Stripe (task 942): this line names the plan the account is
  // CHARGED for, so the Stripe-billed plan wins when the summary read answers.
  // The AMS entitlement plan, when it deliberately differs, is marked below
  // rather than passed off as the billed one.
  const billed = summary?.billedPlan ?? null;
  const entitlement = summary?.entitlementPlan ?? null;
  const isEntitlementOverride = entitlement?.isOverride === true;
  const billedLabel =
    billed?.name ?? (billed?.slug ? billed.slug.charAt(0).toUpperCase() + billed.slug.slice(1) : null);
  const displayPlanLabel = billedLabel ?? planLabel;

  /**
   * The account holds a plan but Stripe bills nothing for it (task 1021) — the
   * Free plan, which is never Stripe-provisioned, or a summary read that has
   * ANSWERED and reported no subscription. `undefined` (in flight) and `null`
   * (read unavailable) are not evidence of anything, so neither pushes a paying
   * account onto the Checkout path.
   */
  const currentPlanIsFree = currentPlan !== undefined && isFreePlan(currentPlan);
  const noStripeSubscription =
    currentPlanIsFree ||
    (summary != null && summary.status === null && summary.billedPlan === null);

  // --- Derived endpoint / usage figures --------------------------------------
  const deployedEndpoints = usage?.deployedEndpoints;
  const extraEndpoints =
    hasFiniteEndpointCap && deployedEndpoints !== undefined
      ? Math.max(0, deployedEndpoints - (includedEndpoints as number))
      : 0;
  const extrasMonthlyCents =
    extraEndpoints > 0 && extraEndpointPriceCents !== undefined && extraEndpointPriceCents > 0
      ? extraEndpoints * extraEndpointPriceCents
      : 0;

  const rawUsageDollars =
    usage?.rawCostMicroUsd !== undefined ? usage.rawCostMicroUsd / 1_000_000 : undefined;
  const billedUsageDollars =
    rawUsageDollars !== undefined ? rawUsageDollars * (markup ?? 1) : undefined;

  const openSession = useCallback(async (
    mode: 'checkout' | 'portal',
    planSlug?: string,
    flow?: 'plan' | 'payment_method',
  ) => {
    setRedirecting(true);
    setSessionError(null);

    const returnUrl = `${window.location.origin}/settings/billing?billing_return=1`;

    try {
      const response = await billingApi.createSession({
        mode,
        returnUrl,
        ...(mode === 'checkout' && planSlug ? { planSlug } : {}),
        // task 943: each action names its destination instead of every button
        // opening the same portal home.
        ...(mode === 'portal' && flow ? { flow } : {}),
      });
      window.location.href = response.data.data.url;
    } catch (err: unknown) {
      const e = err as { status?: number };
      if (e?.status === 404 || e?.status === 501 || !e?.status) {
        setSessionError(
          mode === 'checkout'
            ? 'Upgrade is not available yet. Please contact support to upgrade your plan.'
            : 'Billing management is not available yet. Please contact support.'
        );
      } else {
        setSessionError('Something went wrong. Please try again.');
      }
      setRedirecting(false);
    }
  }, []);

  // Plan not known yet — hold the render rather than flash "No plan yet" at
  // an account that has a plan (task 676).
  if (planPending) return null;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <PageHeader
        title="Plan & Billing"
        description="Your plan, the endpoints and usage you're billed for this period, and billing management. Payment is handled securely by Stripe."
      />

      {/* Current plan */}
      <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="sm:flex sm:items-start sm:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Current plan
            </h2>
            <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-testid="billing-current-plan">
              {displayPlanLabel ?? 'No plan yet'}
            </p>
            {isEntitlementOverride && entitlement && (
              <p
                className="mt-1 text-sm text-gray-600 dark:text-gray-300"
                data-testid="billing-entitlement-override"
              >
                Entitlements:{' '}
                <span className="font-medium text-gray-800 dark:text-gray-100">{entitlement.name}</span>{' '}
                <span className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-200 text-xs font-semibold">
                  override
                </span>
              </p>
            )}
            {hasNoPlan && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-testid="billing-no-plan-note">
                Your account doesn't have a plan yet — choose a plan below to get started.
              </p>
            )}
          </div>
          {canManageBilling && (
            <div className="mt-4 sm:mt-0 flex items-center gap-2">
              {hasNoPlan ? (
                <button
                  onClick={() =>
                    document
                      .getElementById('billing-plan-catalog')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                  data-testid="billing-upgrade-button"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-medium rounded-md transition-colors"
                >
                  <ArrowUpCircleIcon className="h-4 w-4" />
                  Choose a plan
                </button>
              ) : null}
            </div>
          )}
        </div>

        {!canManageBilling && (
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400" data-testid="billing-member-note">
            Plan and billing are managed by your account owner.
          </p>
        )}

        {sessionError && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" data-testid="billing-session-error">
            {sessionError}
          </p>
        )}

        {/* Curated plan terms from claims */}
        {!hasNoPlan && (
          <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="rounded-md bg-gray-50 dark:bg-gray-700/50 px-4 py-3">
              <dt className="text-xs text-gray-500 dark:text-gray-400">Endpoints included</dt>
              <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white" data-testid="billing-included-endpoints">
                {includedEndpoints === undefined
                  ? '—'
                  : includedEndpoints === -1
                    ? 'Unlimited'
                    : String(includedEndpoints)}
              </dd>
            </div>
            <div className="rounded-md bg-gray-50 dark:bg-gray-700/50 px-4 py-3">
              <dt className="text-xs text-gray-500 dark:text-gray-400">Usage billing rate</dt>
              <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white" data-testid="billing-usage-rate">
                {markup === null ? '—' : `${markup.toFixed(2)}× LLM cost`}
              </dd>
            </div>
            {extraEndpointPriceCents !== undefined && extraEndpointPriceCents > 0 && (
              <div className="rounded-md bg-gray-50 dark:bg-gray-700/50 px-4 py-3">
                <dt className="text-xs text-gray-500 dark:text-gray-400">Extra endpoint price</dt>
                <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white" data-testid="billing-extra-endpoint-price">
                  {formatCentsAsUsd(extraEndpointPriceCents)}/mo each
                </dd>
              </div>
            )}
          </dl>
        )}

        {features.length > 0 && (
          <div className="mt-4">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Included features
            </h3>
            <ul className="mt-2 flex flex-wrap gap-2">
              {features.map((feature) => (
                <li
                  key={feature.key}
                  className="px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 text-xs font-medium"
                >
                  {feature.name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Endpoints: included vs used + extras billed */}
      {usage && deployedEndpoints !== undefined && (
        <section
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
          data-testid="billing-endpoints-section"
        >
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Endpoints
          </h2>
          <p className="mt-3 text-2xl font-semibold text-gray-900 dark:text-white" data-testid="billing-endpoints-usage">
            {deployedEndpoints}
            {hasFiniteEndpointCap ? (
              <span className="text-base font-normal text-gray-500 dark:text-gray-400">
                {' '}of {includedEndpoints} included deployed
              </span>
            ) : (
              <span className="text-base font-normal text-gray-500 dark:text-gray-400"> deployed</span>
            )}
          </p>

          {hasFiniteEndpointCap && (
            <div className="mt-3 h-2 w-full max-w-md rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  extraEndpoints > 0 ? 'bg-amber-500' : 'bg-indigo-500'
                }`}
                style={{
                  width: `${Math.min(100, (deployedEndpoints / Math.max(1, includedEndpoints as number)) * 100)}%`,
                }}
              />
            </div>
          )}

          {extraEndpoints > 0 && (
            <p className="mt-3 text-sm text-amber-700 dark:text-amber-400" data-testid="billing-endpoint-extras">
              {extraEndpoints} extra endpoint{extraEndpoints === 1 ? '' : 's'} beyond your plan
              {extrasMonthlyCents > 0 && (
                <>
                  {' '}— billed at{' '}
                  <span className="font-medium">{formatCentsAsUsd(extrasMonthlyCents)}/mo</span>
                  {extraEndpointPriceCents !== undefined && (
                    <span className="text-gray-500 dark:text-gray-400">
                      {' '}({extraEndpoints} × {formatCentsAsUsd(extraEndpointPriceCents)})
                    </span>
                  )}
                </>
              )}
              .
            </p>
          )}
        </section>
      )}

      {/* Usage cost this period — billed from the first dollar (no cap / no allowance) */}
      {usage && billedUsageDollars !== undefined && (
        <section
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
          data-testid="billing-usage-cost-section"
        >
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Usage cost this period ({usage.period})
          </h2>
          <p className="mt-3 text-2xl font-semibold text-gray-900 dark:text-white" data-testid="billing-usage-cost">
            {formatUsd(billedUsageDollars)}
            <span className="text-base font-normal text-gray-500 dark:text-gray-400"> accrued</span>
          </p>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {usage.endpointCallCount} endpoint call{usage.endpointCallCount === 1 ? '' : 's'} from
            deployed endpoints and embeds.{' '}
            {markup !== null && rawUsageDollars !== undefined ? (
              <>
                Billed as actual LLM cost ({formatUsd(rawUsageDollars)}) × {markup.toFixed(2)} markup,
                additive to your plan — every dollar of external usage is billed, with no free
                allowance.
              </>
            ) : (
              <>
                Billed as actual LLM usage cost, additive to your plan — every dollar of external
                usage is billed, with no free allowance.
              </>
            )}{' '}
            In-app testing and Composer runs are free. Final charges settle at period close.
          </p>
        </section>
      )}

      {/* Plan catalog — rendered from the live AMS catalog, never hardcoded */}
      <section id="billing-plan-catalog">
        <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
          Plans
        </h2>

        {catalogLoading && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="billing-plan-cards-loading"
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-56 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 animate-pulse"
              >
                <div className="h-5 w-24 rounded bg-gray-200 dark:bg-gray-700" />
                <div className="mt-3 h-8 w-20 rounded bg-gray-200 dark:bg-gray-700" />
                <div className="mt-4 space-y-2">
                  <div className="h-3 w-full rounded bg-gray-100 dark:bg-gray-700/60" />
                  <div className="h-3 w-5/6 rounded bg-gray-100 dark:bg-gray-700/60" />
                  <div className="h-3 w-4/6 rounded bg-gray-100 dark:bg-gray-700/60" />
                </div>
              </div>
            ))}
          </div>
        )}

        {catalogError && !catalogLoading && (
          <p className="text-sm text-red-600 dark:text-red-400" data-testid="billing-catalog-error">
            Plans are unavailable right now. Your current plan and usage above are unaffected —
            please try again shortly.
          </p>
        )}

        {catalog && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {catalog.map((plan) => {
              const isCurrent = planSlug === plan.slug;
              // Free is $0, not custom priced (task 1021) — only the
              // contact-sales tier keeps the Custom price and the sales line.
              const isFree = isFreePlan(plan);
              const isCustomPriced = !plan.selfServe && plan.basePriceCents === 0 && !isFree;
              // Checkout buttons render for every account with no Stripe
              // subscription to modify — no plan at all, or Free (task 1021).
              // Stripe Checkout cannot modify an existing subscription; a
              // Stripe-subscribed account changes tiers through the Portal.
              const showAction =
                canManageBilling &&
                plan.selfServe &&
                !isCurrent &&
                (hasNoPlan || noStripeSubscription);
              return (
                <div
                  key={plan.slug}
                  data-testid={`billing-plan-card-${plan.slug}`}
                  className={`flex flex-col rounded-lg border p-5 bg-white dark:bg-gray-800 ${
                    isCurrent
                      ? 'border-indigo-500 ring-1 ring-indigo-500'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{plan.name}</h3>
                    {isCurrent && (
                      <span className="px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 text-xs font-medium">
                        Current plan
                      </span>
                    )}
                  </div>
                  <p className="mt-1">
                    <span className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {isCustomPriced ? 'Custom' : formatCentsAsWholeUsd(plan.basePriceCents)}
                    </span>
                    {!isCustomPriced && !isFree && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">/mo</span>
                    )}
                  </p>
                  {PLAN_TAGLINES[plan.slug] && (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{PLAN_TAGLINES[plan.slug]}</p>
                  )}
                  <ul className="mt-4 space-y-2 flex-1">
                    {planCardBullets(plan).map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <CheckIcon className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                  {showAction && (
                    <button
                      onClick={() => openSession('checkout', plan.slug)}
                      disabled={redirecting}
                      data-testid={`billing-plan-card-upgrade-button-${plan.slug}`}
                      className="mt-4 w-full inline-flex justify-center items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-medium rounded-md transition-colors"
                    >
                      {redirecting ? 'Redirecting...' : 'Choose plan'}
                    </button>
                  )}
                  {/* Contact sales belongs to the tier that is genuinely custom
                      priced. Free is not self-serve either, but it needs no
                      action at all — every account has it (task 1021). */}
                  {!plan.selfServe && !isCurrent && !isFree && (
                    <p className="mt-4 text-center text-sm font-medium text-indigo-600 dark:text-indigo-400">
                      Contact sales
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Each plan includes a set number of endpoints; extra endpoints and metered LLM usage are
          billed on top of the plan fee. Choosing a plan opens Stripe Checkout. Cards, invoices,
          cancellation and changes between paid plans are managed in the Stripe Billing Portal via
          Manage billing — Innovate never sees your payment details.
        </p>
      </section>
    </div>
  );
}
